import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSignup } from '../../hooks/useSignup';
import { useLang } from '../../hooks/useLang';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookOpen, faEye, faEyeSlash, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import '../../styles/auth/Signup.scss';

const Signup = () => {
    const { urlLanguage } = useLang();
    const navigate = useNavigate();

    const [step, setStep] = useState('form'); // 'form' | 'verify'
    const [sendingCode, setSendingCode] = useState(false);

    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [termAccepted, setTermAccepted] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [cooldownTimer, setCooldownTimer] = useState(0);
    const [successMessage, setSuccessMessage] = useState(null);
    const [passwordStrength, setPasswordStrength] = useState(0);

    const { signup, error, isLoading } = useSignup();
    const [signupError, setSignupError] = useState(null);

    const getPasswordStrength = (pwd) => {
        let score = 0;
        if (pwd.length >= 8) score++;
        if (/[A-Z]/.test(pwd)) score++;
        if (/[a-z]/.test(pwd)) score++;
        if (/[0-9]/.test(pwd)) score++;
        if (/[^A-Za-z0-9]/.test(pwd)) score++;
        return Math.min(4, Math.floor(score / 1.25));
    };

    const strengthLabels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
    const strengthColors = ['', '#e74c3c', '#e67e22', '#f1c40f', '#27ae60'];

    const handlePasswordChange = (e) => {
        const val = e.target.value;
        setPassword(val);
        setPasswordStrength(getPasswordStrength(val));
    };

    const isValidPhoneNumber = (number) => {
        const regex = /^\+994(50|51|55|70|77|99)\d{7}$/;
        return regex.test(number);
    };

    const normalizePhoneNumber = (number) => {
        return `+${number.replace(/\s+/g, '').replace(/-/g, '')}`;
    };

    const handleSendCode = async () => {
        setSignupError(null);
        setSuccessMessage(null);
        setSendingCode(true);

        try {
            const response = await fetch('/api/auth/send-code', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            const json = await response.json();

            if (!response.ok) {
                setSignupError(json.error || 'Failed to send verification code.');
                return false;
            }

            setSuccessMessage(`We sent a verification code to ${email}.`);
            setCooldownTimer(60);
            return true;
        } catch (error) {
            setSignupError('Network error while sending verification code.');
            return false;
        } finally {
            setSendingCode(false);
        }
    };

    useEffect(() => {
        let timer;
        if (cooldownTimer > 0) {
            timer = setInterval(() => {
                setCooldownTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [cooldownTimer]);

    const isValidUsername = (name) => /^[a-zA-Z0-9_.]+$/.test(name);

    const handleContinue = async (e) => {
        e.preventDefault();

        if (!termAccepted) {
            return setSignupError('Accept the terms and conditions.');
        }

        if (username.trim().length < 6) {
            return setSignupError('Username must be at least 6 characters long.');
        }

        if (!isValidUsername(username.trim())) {
            return setSignupError('Username can only contain letters, numbers, underscores, and periods.');
        }

        if (passwordStrength < 4) {
            return setSignupError(
                'Password is too weak. Use at least 8 characters with uppercase, lowercase, a number, and a special character.'
            );
        }

        if (password !== confirmPassword) {
            return setSignupError('Passwords do not match.');
        }

        if (!isValidPhoneNumber(normalizePhoneNumber(phoneNumber))) {
            return setSignupError('Phone number is not valid for Azerbaijan (+994).');
        }

        const sent = await handleSendCode();
        if (sent) setStep('verify');
    };

    const handleVerifySubmit = async (e) => {
        e.preventDefault();

        if (!verificationCode || verificationCode.length !== 6) {
            return setSignupError('Please enter the 6-character verification code.');
        }

        setSignupError(null);

        const data = {
            firstname: firstname.trim(),
            lastname: lastname.trim(),
            username: username.trim(),
            email,
            phoneNumber,
            password,
            verificationCode,
        };

        const result = await signup(data);

        if (result && result.success) {
            navigate(urlLanguage('/'));
        } else if (result && result.error) {
            setSignupError(result.error);
        }
    };

    return (
        <div className="signup-page">
            {/* ── Left decorative panel ── */}
            <div className="auth-left-panel">
                {/* Decorative rings */}
                <div className="panel-ring panel-ring--1" />
                <div className="panel-ring panel-ring--2" />
                <div className="panel-ring panel-ring--3" />
                <div className="panel-ring panel-ring--4" />
                <div className="panel-glow" />

                <div className="panel-content">
                    <div className="panel-wordmark">YeniDers</div>
                    <p className="panel-tagline">
                        Your knowledge,<br />
                        <em>supercharged.</em>
                    </p>
                    <div className="panel-divider" />
                    <div className="panel-stats">
                        <div className="panel-stat">
                            <span className="stat-number">50K+</span>
                            <span className="stat-label">Students</span>
                        </div>
                        <div className="panel-stat">
                            <span className="stat-number">200K+</span>
                            <span className="stat-label">Notes shared</span>
                        </div>
                        <div className="panel-stat">
                            <span className="stat-number">98%</span>
                            <span className="stat-label">Satisfaction</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Right form panel ── */}
            <div className="auth-right-panel">
                <div className="auth-form-wrap">
                    {/* Mobile-only logo */}
                    <div className="auth-mobile-logo">
                        <div className="auth-logo-icon-wrap">
                            <FontAwesomeIcon icon={faBookOpen} />
                        </div>
                        YeniDers
                    </div>

                    <Link to="/" className="back-to-home-link">
                        <FontAwesomeIcon icon={faArrowLeft} /> Back to homepage
                    </Link>

                    <div className="auth-header">
                        <h1>Create your account</h1>
                        <p>Join thousands of students and educators today.</p>
                    </div>

                    <div className="auth-card">
                      {step === 'form' && (
                        <form onSubmit={handleContinue}>
                            {(error || signupError) && (
                                <div className="auth-error">{signupError || error}</div>
                            )}

                            <div className="flex-row">
                                <div className="auth-form-group">
                                    <label>First Name</label>
                                    <input
                                        type="text"
                                        placeholder="John"
                                        value={firstname}
                                        onChange={(e) => setFirstname(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className="auth-form-group">
                                    <label>Last Name</label>
                                    <input
                                        type="text"
                                        placeholder="Doe"
                                        value={lastname}
                                        onChange={(e) => setLastname(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="auth-form-group">
                                <label>Username</label>
                                <input
                                    type="text"
                                    placeholder="johndoe123"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    minLength={6}
                                    required
                                />
                                <small className="field-hint">At least 6 characters — letters, numbers, underscores, and periods only.</small>
                            </div>

                            <div className="auth-form-group">
                                <label>Email address</label>
                                <input
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="auth-form-group phone-input-container">
                                <label>Phone Number</label>
                                <PhoneInput
                                    country={'az'}
                                    onlyCountries={['az']}
                                    value={phoneNumber}
                                    onChange={setPhoneNumber}
                                    disableDropdown={true}
                                    specialLabel=""
                                    placeholder="(50) 000-00-00"
                                />
                            </div>

                            <div className="auth-form-group password-group">
                                <label>Password</label>
                                <div className="input-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Create a strong password"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        required
                                    />
                                    <FontAwesomeIcon
                                        className="password-toggle-icon"
                                        icon={showPassword ? faEyeSlash : faEye}
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                </div>
                                {password && (
                                    <div className="password-strength">
                                        <div className="strength-bar">
                                            {[1, 2, 3, 4].map((i) => (
                                                <div
                                                    key={i}
                                                    className="strength-segment"
                                                    style={{
                                                        backgroundColor:
                                                            i <= passwordStrength
                                                                ? strengthColors[passwordStrength]
                                                                : 'hsl(var(--border))',
                                                    }}
                                                />
                                            ))}
                                        </div>
                                        {passwordStrength > 0 && (
                                            <span
                                                className="strength-label"
                                                style={{ color: strengthColors[passwordStrength] }}
                                            >
                                                {strengthLabels[passwordStrength]}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="auth-form-group password-group">
                                <label>Confirm Password</label>
                                <div className="input-wrapper">
                                    <input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        placeholder="Repeat your password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                    />
                                    <FontAwesomeIcon
                                        className="password-toggle-icon"
                                        icon={showConfirmPassword ? faEyeSlash : faEye}
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    />
                                </div>
                            </div>

                            <div className="auth-form-group signup-checkbox-group">
                                <input
                                    type="checkbox"
                                    id="terms"
                                    checked={termAccepted}
                                    onChange={(e) => setTermAccepted(e.target.checked)}
                                />
                                <label htmlFor="terms">
                                    I agree to the{' '}
                                    <Link to={urlLanguage('/terms')}>Terms of Service</Link> and{' '}
                                    <Link to={urlLanguage('/privacy')}>Privacy Policy</Link>
                                </label>
                            </div>

                            <button disabled={sendingCode} type="submit" className="auth-submit-btn">
                                {sendingCode ? 'Sending code…' : 'Continue'}
                            </button>
                        </form>
                      )}

                      {step === 'verify' && (
                        <form onSubmit={handleVerifySubmit}>
                            {(error || signupError) && (
                                <div className="auth-error">{signupError || error}</div>
                            )}
                            {successMessage && !signupError && (
                                <div className="signup-success">{successMessage}</div>
                            )}

                            <p className="verify-step-intro">
                                Enter the 6-character code we sent to <strong>{email}</strong>.
                            </p>

                            <div className="auth-form-group">
                                <label>Verification Code</label>
                                <input
                                    type="text"
                                    placeholder="6-character code"
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value)}
                                    maxLength={6}
                                    autoFocus
                                    required
                                />
                            </div>

                            <button
                                type="button"
                                className="send-code-btn"
                                onClick={handleSendCode}
                                disabled={cooldownTimer > 0 || sendingCode}
                            >
                                {cooldownTimer > 0 ? `Resend code (${cooldownTimer}s)` : 'Resend code'}
                            </button>

                            <button disabled={isLoading} type="submit" className="auth-submit-btn">
                                {isLoading ? 'Creating account…' : 'Verify & Create Account'}
                            </button>

                            <button type="button" className="back-to-form-link" onClick={() => setStep('form')}>
                                <FontAwesomeIcon icon={faArrowLeft} /> Back to edit details
                            </button>
                        </form>
                      )}

                        <div className="auth-footer">
                            Already have an account?
                            <Link to={urlLanguage('/login')}>Log in</Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Signup;
